import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiDownload, FiPrinter, FiSettings, FiFileText, FiLoader } from "react-icons/fi";
import "./SalesTaxReportsPage.css";
import {
  FilingStatus,
  JurisdictionTaxResult,
  MonthlyTaxReportResponse,
  TaxJurisdictionSetting,
  TaxJurisdictionType,
  salesTaxApi,
} from "../../services/salesTaxApi";
import { IEventItem, getEvents, updateEvent } from "../../services/eventsApi";
import { formatMoney } from "../../services/currency";

const money = (cents = 0) => formatMoney(cents / 100);

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const moveMonth = (month: string, delta: number) => {
  const [year, monthNumber] = month.split("-").map(Number);
  const next = new Date(year, monthNumber - 1 + delta, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (month: string) => {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(year, monthNumber - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const calendarDateLabel = (value: string) => {
  const match = value.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const [, year, monthNumber, day] = match;
  return new Date(
    Number(year),
    Number(monthNumber) - 1,
    Number(day),
  ).toLocaleDateString();
};

const eventDateLabel = (event: IEventItem) => {
  const start = event.startDateTime ? new Date(event.startDateTime) : null;
  const end = event.endDateTime ? new Date(event.endDateTime) : null;
  if (!start || Number.isNaN(start.getTime())) return "No date";
  const startLabel = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (!end || Number.isNaN(end.getTime())) return startLabel;
  const endLabel = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
};

const eventEndDate = (event: IEventItem) => {
  const start = new Date(event.startDateTime || "");
  const explicitEnd = event.endDateTime ? new Date(event.endDateTime) : null;
  if (
    explicitEnd &&
    !Number.isNaN(explicitEnd.getTime()) &&
    (!Number.isNaN(start.getTime()) ? explicitEnd > start : true)
  ) {
    return explicitEnd;
  }
  const dailyEnds = (event.dailySchedule || [])
    .map((day) => new Date(day.endDateTime || ""))
    .filter((date) => !Number.isNaN(date.getTime()));
  if (dailyEnds.length) {
    return new Date(Math.max(...dailyEnds.map((date) => date.getTime())));
  }
  if (!Number.isNaN(start.getTime())) {
    return new Date(start.getTime() + 24 * 60 * 60 * 1000);
  }
  return null;
};

const monthBounds = (month: string) => {
  const [year, monthNumber] = month.split("-").map(Number);
  return {
    start: new Date(year, monthNumber - 1, 1),
    endExclusive: new Date(year, monthNumber, 1),
  };
};

const eventOverlapsMonth = (event: IEventItem, month: string) => {
  const start = new Date(event.startDateTime || "");
  const endExclusive = eventEndDate(event);
  if (Number.isNaN(start.getTime()) || !endExclusive) return false;
  const bounds = monthBounds(month);
  return start < bounds.endExclusive && endExclusive > bounds.start;
};

const intervalDateLabel = (startValue: string, endExclusiveValue: string) => {
  const start = new Date(startValue);
  const endExclusive = new Date(endExclusiveValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endExclusive.getTime())) {
    return "";
  }
  const endInclusive = new Date(endExclusive.getTime() - 1);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const startLabel = start.toLocaleDateString("en-US", options);
  const endLabel = endInclusive.toLocaleDateString("en-US", options);
  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
};

const intervalListLabel = (
  intervals?: Array<{ start: string; endExclusive: string }>,
) => {
  const labels = (intervals || [])
    .map((interval) => intervalDateLabel(interval.start, interval.endExclusive))
    .filter(Boolean);
  if (!labels.length) return "";
  if (labels.length <= 3) return labels.join(", ");
  return `${labels.slice(0, 3).join(", ")} + ${labels.length - 3} more`;
};

const isCardPaymentMethod = (value: string) =>
  value.trim().toLowerCase() === "card";

const uniqueByKey = <T,>(rows: T[], keyFor: (row: T) => string) => {
  const map = new Map<string, T>();
  rows.forEach((row) => {
    map.set(keyFor(row), row);
  });
  return Array.from(map.values());
};

const overlapsReportPeriod = (
  row: TaxJurisdictionSetting,
  periodStart?: string,
  periodEndExclusive?: string,
) => {
  if (!row.active) return false;
  if (!periodStart || !periodEndExclusive) return true;
  const startsBeforeEnd = row.effectiveFrom < periodEndExclusive.slice(0, 10);
  const endsAfterStart =
    !row.effectiveTo || row.effectiveTo >= periodStart.slice(0, 10);
  return startsBeforeEnd && endsAfterStart;
};

const allocateCollectedTax = (
  collectedCents: number,
  calculatedRows: Array<{ calculatedTaxCents: number }>,
) => {
  if (!calculatedRows.length) return [];
  const totalCalculated = calculatedRows.reduce(
    (sum, row) => sum + row.calculatedTaxCents,
    0,
  );
  if (totalCalculated === 0) {
    return calculatedRows.map((_, index) =>
      index === calculatedRows.length - 1 ? collectedCents : 0,
    );
  }
  let remaining = collectedCents;
  return calculatedRows.map((row, index) => {
    if (index === calculatedRows.length - 1) return remaining;
    const share = Math.round(
      (collectedCents * row.calculatedTaxCents) / totalCalculated,
    );
    remaining -= share;
    return share;
  });
};

const buildDerivedJurisdictions = (
  rows: Array<
    Pick<
      JurisdictionTaxResult,
      "id" | "code" | "name" | "type" | "rateBasisPoints"
    >
  >,
  taxableSalesCents: number,
  taxCollectedCents: number,
): JurisdictionTaxResult[] => {
  const calculatedRows = rows.map((row) => ({
    ...row,
    taxableSalesCents,
    calculatedTaxCents: Math.round(
      (taxableSalesCents * row.rateBasisPoints) / 10_000,
    ),
  }));
  const allocations = allocateCollectedTax(taxCollectedCents, calculatedRows);
  return calculatedRows.map((row, index) => ({
    ...row,
    taxCollectedCents: allocations[index] || 0,
  }));
};

const mergeJurisdictions = (
  left: JurisdictionTaxResult[],
  right: JurisdictionTaxResult[],
) => {
  const merged = new Map<string, JurisdictionTaxResult>();
  [...left, ...right].forEach((row) => {
    const key = `${row.id}:${row.rateBasisPoints}`;
    const current = merged.get(key);
    if (current) {
      current.taxableSalesCents += row.taxableSalesCents;
      current.taxCollectedCents += row.taxCollectedCents;
      current.calculatedTaxCents += row.calculatedTaxCents;
      return;
    }
    merged.set(key, { ...row });
  });
  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const collapseSpecialDistrictsIntoCity = (rows: JurisdictionTaxResult[]) => {
  const next = [...rows];
  const cityIndex = next.findIndex(
    (row) =>
      row.type === "CITY" &&
      row.name.toLowerCase().includes("new orleans"),
  );
  if (cityIndex < 0) return next;
  const specialDistricts = next.filter((row) => row.type === "SPECIAL_DISTRICT");
  if (!specialDistricts.length) return next;
  const cityRow = { ...next[cityIndex] };
  cityRow.rateBasisPoints += specialDistricts.reduce(
    (sum, row) => sum + row.rateBasisPoints,
    0,
  );
  cityRow.calculatedTaxCents += specialDistricts.reduce(
    (sum, row) => sum + row.calculatedTaxCents,
    0,
  );
  cityRow.taxCollectedCents += specialDistricts.reduce(
    (sum, row) => sum + row.taxCollectedCents,
    0,
  );
  const specialDistrictNames = specialDistricts.map((row) => row.name).join(" + ");
  cityRow.name = `${cityRow.name} + ${specialDistrictNames}`;
  next[cityIndex] = cityRow;
  return next.filter((row) => row.type !== "SPECIAL_DISTRICT");
};

const isLouisianaStateJurisdiction = (row: JurisdictionTaxResult) =>
  row.type === "STATE" && row.name.toLowerCase().includes("louisiana");

const louisianaStateVendorCompensationCents = (
  row: JurisdictionTaxResult,
) => {
  if (!isLouisianaStateJurisdiction(row)) return 0;
  return Math.round(row.calculatedTaxCents * 0.0084);
};

const newOrleansCityVendorCompensationCents = (row: JurisdictionTaxResult) => {
  const name = row.name.toLowerCase();
  const isNewOrleansCitySide =
    row.type === "CITY" &&
    (name.includes("new orleans") || name.includes("orleans parish"));
  if (!isNewOrleansCitySide) return 0;
  return Math.round(row.calculatedTaxCents * 0.01);
};

const filingRateLabel = (row: JurisdictionTaxResult) => {
  if (row.type === "STATE") return "State Tax Rate";
  if (row.type === "SPECIAL_DISTRICT") return "District Tax Rate";
  return "City Tax Rate";
};

const statusLabel: Record<FilingStatus, string> = {
  NOT_FILED: "Not Filed",
  READY_TO_FILE: "Ready to File",
  FILED: "Filed",
  AMENDED: "Amended",
};

const fieldClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const primaryButton =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50";
const secondaryButton =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

const SummaryCard = ({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) => (
  <div className="tax-metric border-l-2 border-slate-200 px-4 py-2">
    <div className="text-xs font-semibold text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
      {value}
    </div>
    {note ? <div className="mt-1 text-xs text-slate-500">{note}</div> : null}
  </div>
);

const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <section className="tax-section border-t border-slate-200 py-6 print:break-inside-avoid">
    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    <div className="mt-4">{children}</div>
  </section>
);

const SquareSetupHelp = () => (
  <span>
    Connect Square from the VendorOrbis mobile app under{" "}
    <strong>Menu &gt; Settings &gt; Billing</strong>.{" "}
    <a className="font-semibold text-blue-700 underline" href="/faq#square">
      View the Square connection FAQ
    </a>
    .
  </span>
);

const JurisdictionCards = ({
  rows,
  emptyMessage,
  combineSpecialDistrictsIntoCity = false,
}: {
  rows: JurisdictionTaxResult[];
  emptyMessage: string;
  combineSpecialDistrictsIntoCity?: boolean;
}) => {
  const displayRows = combineSpecialDistrictsIntoCity
    ? collapseSpecialDistrictsIntoCity(rows)
    : rows;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {displayRows.length ? (
        displayRows.map((row) => {
          const vendorCompensationCents = Math.max(
            louisianaStateVendorCompensationCents(row),
            newOrleansCityVendorCompensationCents(row),
          );
          const estimatedAmountDueCents = Math.max(
            0,
            row.calculatedTaxCents - vendorCompensationCents,
          );
          const isCombinedCity = row.name
            .toLowerCase()
            .includes("french quarter");
          return (
            <div
              key={`${row.id}-${row.rateBasisPoints}`}
              className="rounded-xl border border-blue-100 bg-blue-50 p-4"
            >
              <div className="font-bold text-blue-950">{row.name}</div>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-slate-500">Total Amount Subject to Tax</dt>
                  <dd className="mt-1 text-lg font-bold">
                    {money(row.taxableSalesCents)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">{filingRateLabel(row)}</dt>
                  <dd className="mt-1 text-lg font-bold">
                    {Number((row.rateBasisPoints / 100).toFixed(4))}%
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">
                    {vendorCompensationCents
                      ? "Tax Due Before Vendor Compensation"
                      : "Tax Due"}
                  </dt>
                  <dd className="mt-1 text-lg font-bold">
                    {money(row.calculatedTaxCents)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">
                    {vendorCompensationCents
                      ? "Estimated Vendor Compensation"
                      : "Tax Collected (allocated)"}
                  </dt>
                  <dd className="mt-1 text-lg font-bold">
                    {money(
                      vendorCompensationCents || row.taxCollectedCents,
                    )}
                  </dd>
                </div>
                {vendorCompensationCents ? (
                  <div className="col-span-2 rounded-lg bg-white/70 p-3">
                    <dt className="text-slate-500">Estimated Amount Due</dt>
                    <dd className="mt-1 text-xl font-bold text-slate-950">
                      {money(estimatedAmountDueCents)}
                    </dd>
                  </div>
                ) : null}
                {isCombinedCity ? (
                  <div className="col-span-2 rounded-lg bg-white/70 p-3 text-xs text-slate-600">
                    Includes the New Orleans city rate plus the French Quarter
                    special district, paid together on the city filing side.
                  </div>
                ) : null}
              </dl>
            </div>
          );
        })
      ) : (
        <div className="col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {emptyMessage}
        </div>
      )}
    </div>
  );
};

const emptyJurisdiction = (): TaxJurisdictionSetting => ({
  code: "",
  name: "",
  type: "OTHER",
  rateBasisPoints: 0,
  state: "",
  parish: "",
  city: "",
  specialDistrict: "",
  effectiveFrom: "2000-01-01",
  effectiveTo: null,
  isDefault: true,
  active: true,
});

const newOrleansDefaults = (): TaxJurisdictionSetting[] => [
  {
    ...emptyJurisdiction(),
    code: "LA_STATE",
    name: "Louisiana State",
    type: "STATE",
    rateBasisPoints: 500,
    state: "Louisiana",
    effectiveFrom: "2025-01-01",
  },
  {
    ...emptyJurisdiction(),
    code: "NEW_ORLEANS_CITY",
    name: "City of New Orleans / Orleans Parish",
    type: "CITY",
    rateBasisPoints: 500,
    state: "Louisiana",
    parish: "Orleans",
    city: "New Orleans",
    effectiveFrom: "2025-01-01",
  },
  {
    ...emptyJurisdiction(),
    code: "FQ_EDD",
    name: "French Quarter EDD",
    type: "SPECIAL_DISTRICT",
    rateBasisPoints: 24.5,
    state: "Louisiana",
    parish: "Orleans",
    city: "New Orleans",
    specialDistrict: "French Quarter EDD",
    effectiveFrom: "2026-07-01",
  },
];

export default function SalesTaxReportsPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodMode, setPeriodMode] = useState<"month" | "range">("month");
  const [timezone, setTimezone] = useState("America/Chicago");
  const [report, setReport] = useState<MonthlyTaxReportResponse | null>(null);
  const [settings, setSettings] = useState<TaxJurisdictionSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<FilingStatus>("NOT_FILED");
  const [filedAt, setFiledAt] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [events, setEvents] = useState<IEventItem[]>([]);
  const [includedPaymentMethods, setIncludedPaymentMethods] = useState<
    string[] | null
  >(null);
  const [includedPaymentSources, setIncludedPaymentSources] = useState<
    string[] | null
  >(null);
  const [squareLocationIds, setSquareLocationIds] = useState<string[]>([]);
  const [eventScopeMode, setEventScopeMode] = useState<"all" | "selected">(
    "all",
  );
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [squareLoading, setSquareLoading] = useState(false);
  const [otherMethodsReport, setOtherMethodsReport] =
    useState<MonthlyTaxReportResponse | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [jurisdictions, eventRows] = await Promise.all([
          salesTaxApi.settings(),
          getEvents(),
        ]);
        setSettings(jurisdictions);
        setEvents(eventRows);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Could not load tax settings.",
        );
      } finally {
        setSettingsLoading(false);
      }
    };
    void loadSettings();
  }, []);

  const syncFilingForm = (next: MonthlyTaxReportResponse) => {
    setStatus(next.filing.status || "NOT_FILED");
    setFiledAt(next.filing.filedAt ? next.filing.filedAt.slice(0, 10) : "");
    setAmountPaid(
      next.filing.amountPaidCents == null
        ? ""
        : (next.filing.amountPaidCents / 100).toFixed(2),
    );
    setConfirmationNumber(next.filing.confirmationNumber || "");
    setNotes(next.filing.notes || "");
  };

  const calculate = async () => {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      setOtherMethodsReport(null);
      const activeStartDate = periodMode === "range" ? startDate : "";
      const activeEndDate = periodMode === "range" ? endDate : "";
      if ((activeStartDate && !activeEndDate) || (!activeStartDate && activeEndDate)) {
        setError("Start date and end date are both required for custom range.");
        return;
      }
      const scopedEventIds =
        eventScopeMode === "selected" ? selectedEventIds : [];
      if (eventScopeMode === "selected" && !scopedEventIds.length) {
        setError("Choose at least one event or switch event scope back to all events.");
        return;
      }
      const next = await salesTaxApi.calculate(
        month,
        timezone,
        includedPaymentMethods,
        includedPaymentSources,
        activeStartDate || undefined,
        activeEndDate || undefined,
        squareLocationIds,
        scopedEventIds,
      );
      const selectedMethods =
        next.current.includedPaymentMethods ?? next.current.availablePaymentMethods;
      const nonCardMethods = selectedMethods.filter(
        (method) => !isCardPaymentMethod(method),
      );
      const includesCard = selectedMethods.some(isCardPaymentMethod);
      if (includesCard && nonCardMethods.length) {
        const nonCardOnly = await salesTaxApi.calculate(
          month,
          timezone,
          nonCardMethods,
          includedPaymentSources,
          activeStartDate || undefined,
          activeEndDate || undefined,
          squareLocationIds,
          scopedEventIds,
        );
        setOtherMethodsReport(nonCardOnly);
      }
      setReport(next);
      syncFilingForm(next);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not calculate sales tax.",
      );
    } finally {
      setLoading(false);
    }
  };

  const refreshSquare = async () => {
    if (!report) return;
    try {
      setSquareLoading(true);
      setError("");
      const square = await salesTaxApi.refreshSquare(
        month,
        timezone,
        periodMode === "range" ? startDate || undefined : undefined,
        periodMode === "range" ? endDate || undefined : undefined,
        squareLocationIds,
        eventScopeMode === "selected" ? selectedEventIds : [],
      );
      setReport((current) => (current ? { ...current, square } : current));
      setMessage("Square data refreshed. VendorOrbis records were not changed.");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to refresh Square data.",
      );
    } finally {
      setSquareLoading(false);
    }
  };

  const assignSquarePayment = async (
    squareId: string,
    kind: "payment" | "refund",
    eventId: string,
  ) => {
    try {
      setSquareLoading(true);
      setError("");
      await salesTaxApi.assignSquarePayment(squareId, kind, eventId);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to assign the Square payment.",
      );
      setSquareLoading(false);
      return;
    }
    await refreshSquare();
    setMessage("Square payment assigned to the event.");
  };

  const saveSettings = async () => {
    try {
      setSettingsLoading(true);
      setError("");
      const next = await salesTaxApi.saveSettings(settings);
      setSettings(next);
      setMessage(
        "Tax jurisdiction settings saved. Recalculate the report to apply them.",
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not save tax settings.",
      );
    } finally {
      setSettingsLoading(false);
    }
  };

  const effectiveEventJurisdictionIds = (event: IEventItem) =>
    event.taxReporting?.jurisdictionIds?.length
      ? event.taxReporting.jurisdictionIds.map(String)
      : settings
          .filter((jurisdiction) => jurisdiction.active && jurisdiction.isDefault)
          .map((jurisdiction) => String(jurisdiction._id || ""))
          .filter(Boolean);

  const toggleEventJurisdiction = (
    eventId: string,
    jurisdictionId: string,
    checked: boolean,
  ) => {
    setEvents((current) =>
      current.map((event) => {
        if (event._id !== eventId) return event;
        const selected = effectiveEventJurisdictionIds(event);
        return {
          ...event,
          taxReporting: {
            ...event.taxReporting,
            timezone: event.taxReporting?.timezone || timezone,
            taxableByDefault: event.taxReporting?.taxableByDefault !== false,
            jurisdictionIds: checked
              ? Array.from(new Set([...selected, jurisdictionId]))
              : selected.filter((value) => value !== jurisdictionId),
          },
        };
      }),
    );
  };

  const saveEventAssignments = async () => {
    try {
      setSettingsLoading(true);
      setError("");
      await Promise.all(
        events.map((event) =>
          updateEvent(event._id, {
            selectedTaxes: event.selectedTaxes || [],
            taxReporting: {
              ...event.taxReporting,
              timezone: event.taxReporting?.timezone || timezone,
              taxableByDefault: event.taxReporting?.taxableByDefault !== false,
              jurisdictionIds: effectiveEventJurisdictionIds(event),
            },
          }),
        ),
      );
      setMessage(
        "Event tax-jurisdiction assignments saved. Recalculate the report to apply them.",
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not save event tax assignments.",
      );
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveFiling = async () => {
    try {
      setLoading(true);
      setError("");
      const parsedPaid =
        amountPaid.trim() === "" ? null : Math.round(Number(amountPaid) * 100);
      if (parsedPaid != null && !Number.isFinite(parsedPaid)) {
        setError("Amount paid must be a valid dollar amount.");
        return;
      }
      const next = await salesTaxApi.updateFiling(month, {
        status,
        timezone,
        filedAt: filedAt || undefined,
        amountPaidCents: parsedPaid,
        confirmationNumber,
        notes,
        includedPaymentMethods,
        includedPaymentSources,
      });
      setReport(next);
      syncFilingForm(next);
      setMessage(
        status === "FILED" || status === "AMENDED"
          ? "Filing snapshot saved. Future transaction changes will be compared with it."
          : "Filing status saved.",
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not save filing status.",
      );
    } finally {
      setLoading(false);
    }
  };

  const filingJurisdictions = useMemo(
    () =>
      report?.current.jurisdictions.filter(
        (row) =>
          row.type === "STATE" ||
          row.type === "CITY" ||
          row.type === "SPECIAL_DISTRICT",
      ) || [],
    [report],
  );
  const availablePaymentMethods = report?.current.availablePaymentMethods ?? [];
  const availablePaymentSources = report?.current.availablePaymentSources ?? [];
  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        const left = new Date(a.startDateTime || 0).getTime();
        const right = new Date(b.startDateTime || 0).getTime();
        return right - left;
      }),
    [events],
  );
  const filteredMonthEvents = useMemo(
    () => sortedEvents.filter((event) => eventOverlapsMonth(event, month)),
    [sortedEvents, month],
  );

  useEffect(() => {
    if (eventScopeMode !== "selected") return;
    const visibleIds = new Set(filteredMonthEvents.map((event) => event._id));
    setSelectedEventIds((current) =>
      current.filter((eventId) => visibleIds.has(eventId)),
    );
  }, [eventScopeMode, filteredMonthEvents]);

  const isCustomRange = periodMode === "range" && Boolean(startDate && endDate);
  const isEventScoped = eventScopeMode === "selected" && selectedEventIds.length > 0;
  const isComparisonReport = isCustomRange || isEventScoped;
  const reportLabel = isEventScoped
    ? `${selectedEventIds.length} selected event${
        selectedEventIds.length === 1 ? "" : "s"
      }`
    : isCustomRange
      ? `${startDate} to ${endDate}`
      : monthLabel(month);
  const currentIncludedPaymentMethods =
    report?.current.includedPaymentMethods ?? null;
  const currentIncludedPaymentSources =
    report?.current.includedPaymentSources ?? null;
  const filteredPaymentScopeParts = [
    currentIncludedPaymentMethods !== null
      ? currentIncludedPaymentMethods.length
        ? `Methods: ${currentIncludedPaymentMethods.join(", ")}`
        : "Methods: none"
      : null,
    currentIncludedPaymentSources !== null
      ? currentIncludedPaymentSources.length
        ? `Sources: ${currentIncludedPaymentSources.join(", ")}`
        : "Sources: none"
      : null,
  ].filter(Boolean) as string[];

  const filingJurisdictionSeeds = useMemo(() => {
    const reportRows = filingJurisdictions.map((row) => ({
      id: row.id,
      code: row.code,
      name: row.name,
      type: row.type,
      rateBasisPoints: row.rateBasisPoints,
    }));
    const settingRows = settings
      .filter(
        (row) =>
          (row.type === "STATE" ||
            row.type === "CITY" ||
            row.type === "SPECIAL_DISTRICT") &&
          overlapsReportPeriod(
            row,
            report?.current.periodStart,
            report?.current.periodEndExclusive,
          ),
      )
      .map((row) => ({
        id: row._id || `${row.code}-${row.rateBasisPoints}`,
        code: row.code,
        name: row.name,
        type: row.type,
        rateBasisPoints: row.rateBasisPoints,
      }));
    return uniqueByKey(
      [...reportRows, ...settingRows],
      (row) => `${row.code}:${row.type}:${row.rateBasisPoints}`,
    );
  }, [
    filingJurisdictions,
    report?.current.periodEndExclusive,
    report?.current.periodStart,
    settings,
  ]);

  const selectedPaymentMethods =
    currentIncludedPaymentMethods ?? availablePaymentMethods;
  const includesCard = selectedPaymentMethods.some(isCardPaymentMethod);
  const selectedNonCardMethods = selectedPaymentMethods.filter(
    (method) => !isCardPaymentMethod(method),
  );
  const squareCardJurisdictions = useMemo(() => {
    if (!report?.square.snapshot || !filingJurisdictionSeeds.length) return [];
    return buildDerivedJurisdictions(
      filingJurisdictionSeeds,
      Math.max(0, report.square.snapshot.salesCents || 0),
      Math.max(0, report.square.snapshot.taxCents || 0),
    );
  }, [filingJurisdictionSeeds, report?.square.snapshot]);
  const otherPaymentJurisdictions = useMemo(
    () =>
      otherMethodsReport?.current.jurisdictions.filter(
        (row) =>
          row.type === "STATE" ||
          row.type === "CITY" ||
          row.type === "SPECIAL_DISTRICT",
      ) || [],
    [otherMethodsReport],
  );
  const combinedSelectedJurisdictions = useMemo(
    () =>
      mergeJurisdictions(squareCardJurisdictions, otherPaymentJurisdictions),
    [otherPaymentJurisdictions, squareCardJurisdictions],
  );
  const hasCombinedNewOrleansCityRate = useMemo(
    () =>
      filingJurisdictionSeeds.some(
        (row) =>
          row.type === "CITY" &&
          row.name.toLowerCase().includes("new orleans") &&
          row.rateBasisPoints > 500,
      ),
    [filingJurisdictionSeeds],
  );
  const hasFrenchQuarterDistrict = useMemo(
    () =>
      filingJurisdictionSeeds.some(
        (row) =>
          row.type === "SPECIAL_DISTRICT" &&
          `${row.code} ${row.name}`.toLowerCase().includes("french quarter"),
      ),
    [filingJurisdictionSeeds],
  );

  const updateSetting = (
    index: number,
    patch: Partial<TaxJurisdictionSetting>,
  ) => {
    setSettings((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );
  };

  const toggleEvent = (eventId: string) => {
    setExpandedEvents((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  return (
    <main className="sales-tax-page min-h-[calc(100vh-56px)] bg-white px-4 py-6 md:px-8 print:p-0">
      <div className="mx-auto max-w-7xl space-y-2">
        <header className="flex flex-wrap items-center justify-between gap-4 pb-6 print:hidden">
          <div>
            <button
              type="button"
              className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-blue-600"
              onClick={() => navigate("/finance")}
            >
              <FiArrowLeft aria-hidden="true" /> Finance
            </button>
            <h1 className="mt-3 text-2xl font-bold text-slate-950">
              Sales tax
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              Tax liability, Square reconciliation, and filing records.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className={secondaryButton}
              onClick={() => setSettingsOpen((value) => !value)}
              aria-expanded={settingsOpen}
            >
              <FiSettings aria-hidden="true" /> Tax settings
            </button>
            <button
              className={secondaryButton}
              disabled={!report}
              onClick={() => window.print()}
              title="Print / Save PDF"
              aria-label="Print / Save PDF"
            >
              <FiPrinter size={18} aria-hidden="true" />
            </button>
            <button
              className={secondaryButton}
              disabled={!report}
              title="Export CSV"
              aria-label="Export CSV"
              onClick={() =>
                void salesTaxApi.exportCsv(
                  month,
                  timezone,
                  includedPaymentMethods,
                  includedPaymentSources,
                  startDate || undefined,
                  endDate || undefined,
                  eventScopeMode === "selected" ? selectedEventIds : [],
                )
              }
            >
              <FiDownload size={18} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="hidden print:block">
          <h1 className="text-2xl font-bold">
            Monthly Sales Tax Report - {reportLabel}
          </h1>
          <p className="text-sm text-slate-600">Timezone: {timezone}</p>
        </div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            {message}
          </div>
        ) : null}

        <section className="tax-filters border-y border-slate-200 py-5">
          <div className="space-y-4 print:hidden">
            <div className="tax-period-toolbar">
              <div>
              <div className="mb-1 text-xs font-semibold text-slate-500">Reporting period</div>
              <div className="inline-flex rounded-lg bg-slate-100 p-1" role="group" aria-label="Reporting period mode">
                <button
                  type="button"
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    periodMode === "month"
                      ? "bg-white text-slate-950 shadow-sm"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  onClick={() => setPeriodMode("month")}
                  aria-pressed={periodMode === "month"}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    periodMode === "range"
                      ? "bg-white text-slate-950 shadow-sm"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  onClick={() => setPeriodMode("range")}
                  aria-pressed={periodMode === "range"}
                >
                  Custom range
                </button>
              </div>
              </div>

            <div>
              <label htmlFor="tax-filing-month" className="mb-1 block text-xs font-semibold text-slate-500">Filing month</label>
              <div className="flex items-center gap-1">
              <button
                className="tax-month-step"
                title="Previous month"
                aria-label="Previous month"
                onClick={() => setMonth((value) => moveMonth(value, -1))}
              >
                <FiChevronLeft size={18} />
              </button>
                <input
                  id="tax-filing-month"
                  type="month"
                  className={fieldClass}
                  value={month}
                  onChange={(event) => { if (event.target.value) setMonth(event.target.value); }}
                />
              <button
                className="tax-month-step"
                title="Next month"
                aria-label="Next month"
                onClick={() => setMonth((value) => moveMonth(value, 1))}
              >
                <FiChevronRight size={18} />
              </button>
              </div>
            </div>
              <label className="min-w-0 text-xs font-semibold text-slate-500">
                Business timezone
                <input
                  className={`${fieldClass} mt-1 text-slate-700`}
                  value={timezone}
                  onChange={(event) => setTimezone(event.target.value)}
                />
              </label>
              <div className="flex items-end">
                <button
                  className={`${primaryButton} w-full`}
                  disabled={loading || !month || (eventScopeMode === "selected" && !selectedEventIds.length)}
                  onClick={() => void calculate()}
                >
                  {loading ? <FiLoader className="animate-spin" aria-hidden="true" /> : <FiFileText aria-hidden="true" />}
                  {loading ? "Calculating..." : "Calculate tax"}
                </button>
              </div>
            </div>

            {periodMode === "range" ? (
              <div className="border-t border-slate-100 pt-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(180px,220px),minmax(180px,220px),auto]">
                  <label className="text-sm font-semibold text-slate-700">
                    Start date
                    <input
                      type="date"
                      className={`${fieldClass} mt-1 bg-white`}
                      value={startDate}
                      onChange={(event) => setStartDate(event.target.value)}
                    />
                  </label>
                  <label className="text-sm font-semibold text-slate-700">
                    End date
                    <input
                      type="date"
                      className={`${fieldClass} mt-1 bg-white`}
                      value={endDate}
                      onChange={(event) => setEndDate(event.target.value)}
                    />
                  </label>
                  <div className="flex items-end">
                    <button
                      className={secondaryButton}
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setPeriodMode("month");
                      }}
                    >
                      Back to month
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="border-t border-slate-100 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    Events <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{settingsLoading ? "Loading..." : `${filteredMonthEvents.length} in ${monthLabel(month)}`}</span>
                  </div>
                </div>
                <div className="inline-flex rounded-lg bg-slate-100 p-1" role="group" aria-label="Event scope">
                  <button
                    type="button"
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      eventScopeMode === "all"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:bg-white"
                    }`}
                    onClick={() => setEventScopeMode("all")}
                    aria-pressed={eventScopeMode === "all"}
                  >
                    All events
                  </button>
                  <button
                    type="button"
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      eventScopeMode === "selected"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:bg-white"
                    }`}
                    onClick={() => setEventScopeMode("selected")}
                    aria-pressed={eventScopeMode === "selected"}
                  >
                    Selected events
                  </button>
                </div>
              </div>
              {eventScopeMode === "selected" ? (
                <div className="mt-4 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="text-xs font-semibold text-blue-600"
                      onClick={() => {
                        setSelectedEventIds(
                          filteredMonthEvents.map((event) => event._id),
                        );
                      }}
                    >
                      Select filing-month events
                    </button>
                    {periodMode === "range" && startDate && endDate ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-blue-600"
                        onClick={() => {
                          const periodStart = new Date(`${startDate}T00:00:00`);
                          const periodEnd = new Date(`${endDate}T23:59:59.999`);
                          setSelectedEventIds(
                            filteredMonthEvents
                              .filter((event) => {
                                const startsAt = new Date(event.startDateTime);
                                return (
                                  startsAt >= periodStart && startsAt <= periodEnd
                                );
                              })
                              .map((event) => event._id),
                          );
                        }}
                      >
                        Select custom-range events
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-xs font-semibold text-slate-600"
                      onClick={() => setSelectedEventIds([])}
                    >
                      Clear events
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                    {filteredMonthEvents.length ? (
                      filteredMonthEvents.map((event) => {
                        const checked = selectedEventIds.includes(event._id);
                        return (
                          <label
                            key={event._id}
                            className={`flex cursor-pointer items-start gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0 ${
                              checked ? "bg-blue-50" : "bg-white"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={checked}
                              onChange={(changeEvent) =>
                                setSelectedEventIds((current) =>
                                  changeEvent.target.checked
                                    ? Array.from(new Set([...current, event._id]))
                                    : current.filter((id) => id !== event._id),
                                )
                              }
                            />
                            <span>
                              <span className="block text-sm font-semibold text-slate-800">
                                {event.title || "Untitled event"}
                              </span>
                              <span className="block text-xs text-slate-500">
                                {eventDateLabel(event)}
                                {event.location ? ` - ${event.location}` : ""}
                              </span>
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="p-3 text-sm text-slate-500">
                        No events found for {monthLabel(month)}.
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {selectedEventIds.length
                      ? `${selectedEventIds.length} event${
                          selectedEventIds.length === 1 ? "" : "s"
                        } selected. Square will be pulled for those event date windows.`
                      : `Choose one or more ${monthLabel(
                          month,
                        )} events before calculating with selected-event scope.`}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {report ? (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 print:hidden">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-bold text-slate-800">
                    Payments included in calculation
                  </div>
                  <div className="text-xs text-slate-500">
                    Uncheck Cash or any other tender you do not want in this
                    report, then calculate again. Split payments are included
                    proportionally.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="text-xs font-semibold text-blue-600"
                    onClick={() => {
                      setIncludedPaymentMethods(null);
                      setIncludedPaymentSources(null);
                    }}
                  >
                    Select all
                  </button>
                  <button
                    className="text-xs font-semibold text-slate-600"
                    onClick={() => {
                      setIncludedPaymentMethods([]);
                      setIncludedPaymentSources([]);
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
              {availablePaymentMethods.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {availablePaymentMethods.map((method) => {
                    const checked =
                      includedPaymentMethods === null ||
                      includedPaymentMethods.includes(method);
                    return (
                      <label
                        key={method}
                        className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                          checked
                            ? "border-blue-300 bg-blue-50 text-blue-800"
                            : "border-slate-300 bg-white text-slate-500"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => {
                            const all = availablePaymentMethods;
                            const current =
                              includedPaymentMethods === null
                                ? all
                                : includedPaymentMethods;
                            setIncludedPaymentMethods(
                              event.target.checked
                                ? Array.from(new Set([...current, method]))
                                : current.filter((value) => value !== method),
                            );
                          }}
                        />
                        {method}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-500">
                  No payment methods were returned for this report yet.
                  Calculate the month first, or check whether those transactions
                  have payment methods stored.
                </div>
              )}
              {availablePaymentSources.length ? (
                <div className="mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Payment sources
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {availablePaymentSources.map((source) => {
                      const checked =
                        includedPaymentSources === null ||
                        includedPaymentSources.includes(source);
                      return (
                        <label
                          key={source}
                          className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                            checked
                              ? "border-blue-300 bg-blue-50 text-blue-800"
                              : "border-slate-300 bg-white text-slate-500"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => {
                              const all = availablePaymentSources;
                              const current =
                                includedPaymentSources === null
                                  ? all
                                  : includedPaymentSources;
                              setIncludedPaymentSources(
                                event.target.checked
                                  ? Array.from(new Set([...current, source]))
                                  : current.filter((value) => value !== source),
                              );
                            }}
                          />
                          {source}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="hidden print:block mt-3 text-xl font-bold text-slate-900">
            {reportLabel}
          </div>
        </section>

        {settingsOpen ? (
          <Section
            title="Tax jurisdiction settings"
            subtitle="Rates are stored as exact basis points, can have effective dates, and may be assigned to individual events later. One percent equals 100 basis points."
          >
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                className={secondaryButton}
                onClick={() => setSettings(newOrleansDefaults())}
              >
                Use Louisiana + New Orleans + French Quarter defaults
              </button>
              <button
                className={secondaryButton}
                onClick={() =>
                  setSettings((current) => [...current, emptyJurisdiction()])
                }
              >
                Add jurisdiction
              </button>
              <button
                className={primaryButton}
                disabled={settingsLoading}
                onClick={() => void saveSettings()}
              >
                Save tax settings
              </button>
            </div>
            <div className="space-y-3">
              {settings.length ? (
                settings.map((row, index) => (
                  <div
                    key={`${row._id || "new"}-${index}`}
                    className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-2 xl:grid-cols-6"
                  >
                    <label className="text-xs font-semibold text-slate-600">
                      Name
                      <input
                        className={`${fieldClass} mt-1`}
                        value={row.name}
                        onChange={(event) =>
                          updateSetting(index, { name: event.target.value })
                        }
                      />
                    </label>
                    <label className="text-xs font-semibold text-slate-600">
                      Code
                      <input
                        className={`${fieldClass} mt-1`}
                        value={row.code}
                        onChange={(event) =>
                          updateSetting(index, {
                            code: event.target.value.toUpperCase(),
                          })
                        }
                      />
                    </label>
                    <label className="text-xs font-semibold text-slate-600">
                      Type
                      <select
                        className={`${fieldClass} mt-1`}
                        value={row.type}
                        onChange={(event) =>
                          updateSetting(index, {
                            type: event.target.value as TaxJurisdictionType,
                          })
                        }
                      >
                        {[
                          "STATE",
                          "PARISH",
                          "CITY",
                          "SPECIAL_DISTRICT",
                          "OTHER",
                        ].map((type) => (
                          <option key={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs font-semibold text-slate-600">
                      Rate %
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        className={`${fieldClass} mt-1`}
                        value={row.rateBasisPoints / 100}
                        onChange={(event) =>
                          updateSetting(index, {
                            rateBasisPoints:
                              Number(event.target.value || 0) * 100,
                          })
                        }
                      />
                    </label>
                    <label className="text-xs font-semibold text-slate-600">
                      Effective from
                      <input
                        type="date"
                        className={`${fieldClass} mt-1`}
                        value={(row.effectiveFrom || "").slice(0, 10)}
                        onChange={(event) =>
                          updateSetting(index, {
                            effectiveFrom: event.target.value,
                          })
                        }
                      />
                    </label>
                    <div className="flex items-end gap-3 pb-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={row.isDefault}
                          onChange={(event) =>
                            updateSetting(index, {
                              isDefault: event.target.checked,
                            })
                          }
                        />
                        Default
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={row.active}
                          onChange={(event) =>
                            updateSetting(index, {
                              active: event.target.checked,
                            })
                          }
                        />
                        Active
                      </label>
                      <button
                        className="text-sm font-semibold text-red-600"
                        onClick={() =>
                          setSettings((current) =>
                            current.filter((_, rowIndex) => rowIndex !== index),
                          )
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                  No jurisdiction rates saved yet. Add one or load the
                  Louisiana/New Orleans defaults.
                </div>
              )}
            </div>
            {settings.some((jurisdiction) => jurisdiction.active && jurisdiction._id) ? (
              <div className="mt-6 border-t border-slate-200 pt-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-slate-900">
                      Assign jurisdictions to events
                    </h3>
                    <p className="text-sm text-slate-500">
                      Defaults apply automatically. Add the Flea Market district
                      only to events where that additional rate applies.
                    </p>
                  </div>
                  <button
                    className={primaryButton}
                    disabled={settingsLoading}
                    onClick={() => void saveEventAssignments()}
                  >
                    Save event assignments
                  </button>
                </div>
                <div className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1">
                  {events.map((event) => {
                    const selected = effectiveEventJurisdictionIds(event);
                    return (
                      <div
                        key={event._id}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="font-semibold text-slate-900">
                          {event.title}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {settings
                            .filter(
                              (jurisdiction) =>
                                jurisdiction.active && jurisdiction._id,
                            )
                            .map((jurisdiction) => {
                              const id = String(jurisdiction._id);
                              const checked = selected.includes(id);
                              return (
                                <label
                                  key={id}
                                  className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                                    checked
                                      ? "border-blue-300 bg-blue-50 text-blue-800"
                                      : "border-slate-300 bg-white text-slate-500"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(changeEvent) =>
                                      toggleEventJurisdiction(
                                        event._id,
                                        id,
                                        changeEvent.target.checked,
                                      )
                                    }
                                  />
                                  {jurisdiction.name} -{" "}
                                  {Number(
                                    (jurisdiction.rateBasisPoints / 100).toFixed(
                                      4,
                                    ),
                                  )}
                                  %
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </Section>
        ) : null}

        {report ? (
          <>
            {report.differsFromFiled ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                <strong>
                  Current calculated liability differs from the filed return.
                </strong>{" "}
                The current liability changed by{" "}
                {money(report.liabilityDifferenceFromFiledCents)}. Review the
                audit and file an amendment if necessary.
              </div>
            ) : null}

            {isComparisonReport ? (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                <strong>Comparison mode:</strong> this report is for
                comparison and export only because it uses{" "}
                {isEventScoped ? "selected events" : "a custom range"}. It does
                not update the saved monthly filing snapshot.
              </div>
            ) : null}

            {filteredPaymentScopeParts.length ? (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <strong>Filtered payment scope:</strong>{" "}
                {filteredPaymentScopeParts.join(" | ")}
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
              <SummaryCard
                label="Gross Sales"
                value={money(report.current.grossSalesCents)}
              />
              <SummaryCard
                label="Discounts"
                value={money(report.current.discountsCents)}
              />
              <SummaryCard
                label="Refunds"
                value={money(report.current.refundsCents)}
              />
              <SummaryCard
                label="Net Sales"
                value={money(report.current.netSalesCents)}
              />
              <SummaryCard
                label="Taxable Sales"
                value={money(report.current.taxableSalesCents)}
              />
              <SummaryCard
                label="Non-taxable Sales"
                value={money(report.current.nonTaxableSalesCents)}
              />
              <SummaryCard
                label="Tax Collected"
                value={money(report.current.taxCollectedCents)}
                note="Actually charged to customers"
              />
              <SummaryCard
                label="Tax Liability"
                value={money(report.current.taxLiabilityCents)}
                note="Calculated from required rates"
              />
              <SummaryCard
                label={
                  report.current.differenceCents < 0
                    ? "Under-collected"
                    : "Over-collected"
                }
                value={money(Math.abs(report.current.differenceCents))}
              />
              <SummaryCard
                label="Transactions / Events"
                value={`${report.current.transactionCount} / ${report.current.eventCount}`}
              />
            </div>

            <Section
              title="Square - Directly Retrieved"
              subtitle="These figures were retrieved directly from your connected Square account. They do not replace VendorOrbis tax-liability calculations."
            >
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3 print:hidden">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800">
                      Source: Square
                    </span>
                    <div className="mt-2 text-xs text-slate-500">
                      {report.square.snapshot
                        ? `Last Square sync: ${new Date(
                            report.square.snapshot.syncedAt,
                          ).toLocaleString()}`
                        : "Square has not been synchronized for this period."}
                    </div>
                  </div>
                  {report.square.snapshot?.locations.length ? (
                    <label className="min-w-[220px] text-sm font-semibold">
                      Square location
                      <select
                        className={`${fieldClass} mt-1`}
                        value={squareLocationIds[0] || "ALL"}
                        onChange={(event) =>
                          setSquareLocationIds(
                            event.target.value === "ALL"
                              ? []
                              : [event.target.value],
                          )
                        }
                      >
                        <option value="ALL">All applicable locations</option>
                        {report.square.snapshot.locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
                <button
                  className={secondaryButton}
                  disabled={squareLoading}
                  onClick={() => void refreshSquare()}
                >
                  {squareLoading ? "Refreshing Square..." : "Refresh Square Data"}
                </button>
              </div>

              {report.square.error ? (
                <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <strong>Unable to refresh Square data.</strong>{" "}
                  {report.square.error.message}
                  {report.square.status === "CACHED"
                    ? " The cached figures below remain available."
                    : " VendorOrbis tax calculations above are still valid."}
                  {!report.square.connected ||
                  report.square.error.code === "SQUARE_DISCONNECTED" ||
                  report.square.error.code === "SQUARE_RECONNECT_REQUIRED" ? (
                    <div className="mt-2">
                      <SquareSetupHelp />
                    </div>
                  ) : null}
                </div>
              ) : null}
              {report.square.differsFromFiled ? (
                <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <strong>Current Square data differs from the filed snapshot.</strong>{" "}
                  The filed Square snapshot remains preserved for audit history.
                </div>
              ) : null}

              {report.square.snapshot ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <SummaryCard
                      label="Completed card payments"
                      value={String(report.square.snapshot.paymentCount)}
                    />
                    <SummaryCard
                      label="Square payment total"
                      value={money(report.square.snapshot.paymentTotalCents)}
                      note="Includes recorded tax and tips"
                    />
                    <SummaryCard
                      label="Square sales"
                      value={
                        report.square.snapshot.salesCents == null
                          ? "Unavailable"
                          : money(report.square.snapshot.salesCents)
                      }
                      note={
                        report.square.snapshot.paymentsWithoutOrderData
                          ? `${report.square.snapshot.paymentsWithoutOrderData} payment(s) lack order itemization`
                          : "Derived from associated Square orders"
                      }
                    />
                    <SummaryCard
                      label="Tax recorded by Square"
                      value={money(report.square.snapshot.taxCents)}
                    />
                    <SummaryCard
                      label="Tips"
                      value={money(report.square.snapshot.tipsCents)}
                    />
                    <SummaryCard
                      label="Discounts"
                      value={money(report.square.snapshot.discountsCents)}
                    />
                    <SummaryCard
                      label="Completed refunds"
                      value={money(report.square.snapshot.refundsCents)}
                      note="Counted by refund date in this reporting period"
                    />
                    <SummaryCard
                      label="Net processed"
                      value={money(report.square.snapshot.netProcessedCents)}
                      note="Payments less completed refunds"
                    />
                    <SummaryCard
                      label="Square processing fees"
                      value={money(report.square.snapshot.processingFeesCents)}
                      note="Reconciliation only; never reduces taxable sales"
                    />
                    <SummaryCard
                      label="Estimated net after fees"
                      value={money(
                        report.square.snapshot.estimatedNetDepositCents,
                      )}
                    />
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-slate-500">
                    Reporting period: {new Date(
                      report.square.snapshot.periodStart,
                    ).toLocaleString()} through {new Date(
                      report.square.snapshot.periodEndExclusive,
                    ).toLocaleString()} (exclusive), {report.square.snapshot.timezone}
                    {report.square.snapshot.locations.length
                      ? ` - Locations: ${report.square.snapshot.locations
                          .map((location) => location.name)
                          .join(", ")}`
                      : ""}
                    {report.square.snapshot.reportIntervals?.length ? (
                      <>
                        <br />
                        Selected event days:{" "}
                        {intervalListLabel(report.square.snapshot.reportIntervals)}
                      </>
                    ) : null}
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 p-5 text-sm text-slate-600">
                  {report.square.connected ? (
                    "Refresh Square Data to retrieve direct payment information for this period."
                  ) : (
                    <SquareSetupHelp />
                  )}
                </div>
              )}
            </Section>

            {report.square.reconciliation ? (
              <Section
                title="Card / Square Reconciliation"
                subtitle="VendorOrbis records and Square API records stay separate. Differences are shown for investigation and never rewrite financial history."
              >
                {report.square.reconciliation.discrepancy ? (
                  <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">
                    <strong>Square discrepancy detected.</strong> Review the
                    transaction-level matches below.
                  </div>
                ) : (
                  <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800">
                    <strong>Square reconciliation matches.</strong>
                  </div>
                )}
                <SquareReconciliationTable
                  reconciliation={report.square.reconciliation}
                  events={events}
                  busy={squareLoading}
                  onAssign={assignSquarePayment}
                />
              </Section>
            ) : null}

            {includesCard ? (
              <Section
                title="Square card filing assistance"
                subtitle="These state and city figures use Square-retrieved card sales and Square-recorded tax, then apply your active VendorOrbis jurisdiction rates."
              >
                {hasCombinedNewOrleansCityRate && !hasFrenchQuarterDistrict ? (
                  <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                    <strong>New Orleans city tax is still combined.</strong>{" "}
                    Your current tax settings appear to use a single New Orleans
                    City rate above 5.00%. For August 2026 French Quarter filing,
                    set City of New Orleans / Orleans Parish to <strong>5.00%</strong>
                    {" "}and add a separate <strong>French Quarter EDD</strong>
                    {" "}special district at <strong>0.245%</strong> effective
                    {" "}July 1, 2026, then recalculate.
                  </div>
                ) : null}
                <JurisdictionCards
                  rows={squareCardJurisdictions}
                  combineSpecialDistrictsIntoCity
                  emptyMessage={
                    report.square.snapshot
                      ? "No filing jurisdictions are configured. Open Tax settings before relying on Square card liability totals."
                      : "Refresh Square data first to build card-based state and city filing totals."
                  }
                />
              </Section>
            ) : null}

            {includesCard && selectedNonCardMethods.length ? (
              <Section
                title="Combined filing assistance for selected payments"
                subtitle={`This combines Square card figures with VendorOrbis-calculated tax for: ${selectedNonCardMethods.join(", ")}.`}
              >
                <JurisdictionCards
                  rows={combinedSelectedJurisdictions}
                  combineSpecialDistrictsIntoCity
                  emptyMessage="No State or City jurisdiction totals are available for the currently selected payment methods."
                />
              </Section>
            ) : !includesCard ? (
              <Section
                title="VendorOrbis filing assistance"
                subtitle="Transfer these taxable-sales and tax-due values to the corresponding return. Special districts are shown separately below."
              >
                <JurisdictionCards
                  rows={filingJurisdictions}
                  emptyMessage="No State or City jurisdiction is configured. Open Tax settings before relying on liability totals."
                />
              </Section>
            ) : null}

            <Section
              title="Tax rate breakdown"
              subtitle="Each effective jurisdiction and rate is calculated separately at transaction level before aggregation."
            >
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Jurisdiction</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3 text-right">Rate</th>
                      <th className="px-4 py-3 text-right">Taxable Sales</th>
                      <th className="px-4 py-3 text-right">Tax Collected</th>
                      <th className="px-4 py-3 text-right">Calculated Tax</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white">
                    {report.current.jurisdictions.map((row) => (
                      <tr key={`${row.id}-${row.rateBasisPoints}`}>
                        <td className="px-4 py-3 font-semibold">{row.name}</td>
                        <td className="px-4 py-3">
                          {row.type.replace(/_/g, " ")}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {Number((row.rateBasisPoints / 100).toFixed(4))}%
                        </td>
                        <td className="px-4 py-3 text-right">
                          {money(row.taxableSalesCents)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {money(row.taxCollectedCents)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {money(row.calculatedTaxCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section
              title="Event breakdown"
              subtitle="Select an event to audit the exact transactions included in this report."
            >
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Event</th>
                      <th className="px-4 py-3 text-right">Taxable Sales</th>
                      <th className="px-4 py-3 text-right">Tax Collected</th>
                      <th className="px-4 py-3 text-right">State Due</th>
                      <th className="px-4 py-3 text-right">City Due</th>
                      <th className="px-4 py-3 text-right">Other Due</th>
                      <th className="px-4 py-3 text-right">Total Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white">
                    {report.current.events.map((event) => (
                      <React.Fragment key={event.eventId}>
                        <tr
                          className="cursor-pointer hover:bg-blue-50"
                          onClick={() => toggleEvent(event.eventId)}
                        >
                          <td className="px-4 py-3 font-semibold">
                            {expandedEvents.has(event.eventId) ? "v" : ">"}
                            {" "}
                            {event.eventName}
                            <div className="text-xs font-normal text-slate-500">
                              {event.transactionCount} transactions
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {money(event.taxableSalesCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {money(event.taxCollectedCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {money(event.stateTaxDueCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {money(event.cityTaxDueCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {money(event.otherTaxDueCents)}
                          </td>
                          <td className="px-4 py-3 text-right font-bold">
                            {money(event.totalTaxDueCents)}
                          </td>
                        </tr>
                        {expandedEvents.has(event.eventId) ? (
                          <tr>
                            <td colSpan={7} className="bg-slate-50 p-3">
                              <TransactionTable
                                rows={report.current.transactions.filter(
                                  (tx) => tx.eventId === event.eventId,
                                )}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section
              title="Payment method breakdown"
              subtitle="For bookkeeping only; payment processor fees are not subtracted from taxable sales."
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {report.current.paymentMethods.map((row) => (
                  <div
                    key={row.paymentMethod}
                    className="rounded-xl border border-slate-200 p-4"
                  >
                    <div className="text-sm font-semibold text-slate-600">
                      {row.paymentMethod}
                    </div>
                    <div className="mt-1 text-xl font-bold">
                      {money(row.totalCollectedCents)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {row.transactionCount} transactions
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              title="Transaction audit"
              subtitle="Sale timestamps determine this report. The table starts with 10 rows to keep the screen manageable."
            >
              <TransactionTable
                rows={report.current.transactions}
                initialVisibleCount={10}
              />
            </Section>

            <Section
              title="Filing status"
              subtitle="Filed and amended statuses freeze a server-side snapshot. Live transaction data is never modified."
            >
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5 print:hidden">
                <label className="text-sm font-semibold">
                  Status
                  <select
                    className={`${fieldClass} mt-1`}
                    value={status}
                    onChange={(event) =>
                      setStatus(event.target.value as FilingStatus)
                    }
                  >
                    {Object.entries(statusLabel).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold">
                  Filed date
                  <input
                    type="date"
                    className={`${fieldClass} mt-1`}
                    value={filedAt}
                    onChange={(event) => setFiledAt(event.target.value)}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Amount paid
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`${fieldClass} mt-1`}
                    value={amountPaid}
                    onChange={(event) => setAmountPaid(event.target.value)}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Confirmation / reference
                  <input
                    className={`${fieldClass} mt-1`}
                    value={confirmationNumber}
                    onChange={(event) =>
                      setConfirmationNumber(event.target.value)
                    }
                  />
                </label>
                <div className="flex items-end">
                  <button
                    className={`${primaryButton} w-full`}
                    disabled={loading || isComparisonReport}
                    onClick={() => void saveFiling()}
                  >
                    Save filing status
                  </button>
                </div>
                <label className="text-sm font-semibold md:col-span-2 xl:col-span-5">
                  Notes
                  <textarea
                    className={`${fieldClass} mt-1 min-h-[90px]`}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                  />
                </label>
              </div>
              {isComparisonReport ? (
                <div className="mt-3 text-sm text-amber-700">
                  Switch back to month-only/all-events mode before saving filing
                  status.
                </div>
              ) : null}
              <div className="mt-3 text-sm text-slate-600">
                Current status: <strong>{statusLabel[report.filing.status]}</strong>
                {report.filing.filedAt
                  ? ` - Filed ${calendarDateLabel(report.filing.filedAt)}`
                  : ""}
                {report.filing.confirmationNumber
                  ? ` - Reference ${report.filing.confirmationNumber}`
                  : ""}
              </div>
            </Section>
          </>
        ) : (
          <div className="tax-empty" role="status" aria-live="polite">
            <div className="tax-empty-icon"><FiFileText size={28} aria-hidden="true" /></div>
            <h2 className="mt-4 text-base font-semibold text-slate-800">{loading ? "Preparing your report" : "No report generated"}</h2>
            <p className="mt-2 text-sm text-slate-500">{reportLabel}</p>
            <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-400">
              <span>Tax liability</span><span>Square reconciliation</span><span>Filing records</span>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function TransactionTable({
  rows,
  initialVisibleCount,
}: {
  rows: MonthlyTaxReportResponse["current"]["transactions"];
  initialVisibleCount?: number;
}) {
  const [visibleCount, setVisibleCount] = useState(
    initialVisibleCount ?? rows.length,
  );

  useEffect(() => {
    setVisibleCount(initialVisibleCount ?? rows.length);
  }, [initialVisibleCount, rows.length]);

  const visibleRows =
    initialVisibleCount == null ? rows : rows.slice(0, visibleCount);
  const hasMore =
    initialVisibleCount != null && visibleCount < rows.length;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-100 text-left text-slate-600">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Event / Items</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2 text-right">Net Sales</th>
              <th className="px-3 py-2 text-right">Taxable</th>
              <th className="px-3 py-2 text-right">Collected</th>
              <th className="px-3 py-2 text-right">Due</th>
              <th className="px-3 py-2 text-right">Difference</th>
            </tr>
          </thead>
          <tbody className="divide-y bg-white">
            {visibleRows.length ? (
              visibleRows.map((tx) => (
                <tr key={tx.id}>
                  <td className="whitespace-nowrap px-3 py-2">
                    {new Date(tx.occurredAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{tx.eventName}</div>
                    <div className="text-slate-500">
                      {tx.lines
                        .map(
                          (line) =>
                            `${line.itemName} x ${Math.abs(line.quantity)}`,
                        )
                        .join(", ") || "Unallocated adjustment"}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(tx.paymentSources || []).map((source) => (
                        <span
                          key={source}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            source.toLowerCase().includes("square")
                              ? "bg-emerald-100 text-emerald-800"
                              : source.toLowerCase().includes("manual")
                                ? "bg-amber-100 text-amber-800"
                                : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {source}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td
                    className={`px-3 py-2 font-semibold ${
                      tx.type === "Refund"
                        ? "text-red-600"
                        : "text-emerald-700"
                    }`}
                  >
                    {tx.type}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(tx.netSalesCents)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(tx.taxableSalesCents)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(tx.taxCollectedCents)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(tx.taxLiabilityCents)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(tx.differenceCents)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-slate-500"
                >
                  No transactions in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {hasMore ? (
        <div className="flex justify-center">
          <button
            type="button"
            className={secondaryButton}
            onClick={() =>
              setVisibleCount((current) =>
                Math.min(current + (initialVisibleCount ?? 10), rows.length),
              )
            }
          >
            Show more
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NeedsEventRow({
  row,
  eventName,
  busy,
  onAssign,
}: {
  row: NonNullable<
    MonthlyTaxReportResponse["square"]["reconciliation"]
  >["audit"][number];
  eventName: (id: string) => string;
  busy: boolean;
  onAssign: (squareId: string, kind: "payment" | "refund", eventId: string) => void;
}) {
  const candidates = row.candidateEventIds || [];
  const [choice, setChoice] = useState(candidates[0] || "");
  const squareId = row.squareRefundId || row.squarePaymentId || "";
  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <span className="whitespace-nowrap">
        {new Date(row.occurredAt).toLocaleString()}
      </span>
      <span className="font-semibold">
        {row.squareRefundId ? "Refund " : ""}
        {money(row.squareAmountCents)}
      </span>
      <span className="font-mono text-xs text-slate-500">{squareId}</span>
      <select
        className="rounded-lg border border-slate-300 px-2 py-1"
        value={choice}
        onChange={(event) => setChoice(event.target.value)}
        aria-label="Event for this Square payment"
      >
        {candidates.map((id) => (
          <option key={id} value={id}>
            {eventName(id)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="rounded-lg bg-slate-900 px-3 py-1 font-semibold text-white disabled:opacity-50"
        disabled={busy || !choice || !squareId}
        onClick={() =>
          onAssign(squareId, row.squareRefundId ? "refund" : "payment", choice)
        }
      >
        Assign
      </button>
    </li>
  );
}

function SquareReconciliationTable({
  reconciliation,
  events = [],
  busy = false,
  onAssign,
}: {
  reconciliation: NonNullable<
    MonthlyTaxReportResponse["square"]["reconciliation"]
  >;
  events?: IEventItem[];
  busy?: boolean;
  onAssign?: (squareId: string, kind: "payment" | "refund", eventId: string) => void;
}) {
  const eventName = (id: string) =>
    events.find((event) => event._id === id)?.title || "Another event";
  const needsEvent = reconciliation.audit.filter(
    (row) => row.status === "NEEDS_EVENT",
  );
  const metrics = [
    {
      label: "Card sales / payment total",
      vendor: reconciliation.vendorCardSalesCents,
      square: reconciliation.squareCardSalesCents,
      difference: reconciliation.cardSalesDifferenceCents,
      count: false,
    },
    {
      label: "Tax collected",
      vendor: reconciliation.vendorTaxCollectedCents,
      square: reconciliation.squareTaxCollectedCents,
      difference: reconciliation.taxDifferenceCents,
      count: false,
    },
    {
      label: "Completed refunds",
      vendor: reconciliation.vendorRefundsCents,
      square: reconciliation.squareRefundsCents,
      difference: reconciliation.refundsDifferenceCents,
      count: false,
    },
    {
      label: "Transactions",
      vendor: reconciliation.vendorTransactionCount,
      square: reconciliation.squareTransactionCount,
      difference: reconciliation.transactionCountDifference,
      count: true,
    },
  ];
  const statusLabel: Record<string, string> = {
    MATCHED: "Square Verified",
    SQUARE_ONLY: "Square Only",
    VENDORORBIS_ONLY: "VendorOrbis Only",
    AMOUNT_MISMATCH: "Amount Mismatch",
    REFUND_MISMATCH: "Refund Mismatch",
    MISSING_SQUARE_ID: "Square / Legacy — Unverified",
    MANUAL_SQUARE_ENTRY: "Manual Entry — Square",
    NEEDS_EVENT: "Needs Event",
  };

  return (
    <div className="space-y-4">
      {needsEvent.length && onAssign ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-bold">
            {needsEvent.length} Square{" "}
            {needsEvent.length === 1 ? "payment" : "payments"} could belong to
            more than one event
          </p>
          <p className="mt-1">
            These were taken while events overlapped, at a Square location both
            events could use (for example, directly in the Square app). They
            aren't counted until you pick the event.
          </p>
          <ul className="mt-2 divide-y divide-amber-200">
            {needsEvent.map((row) => (
              <NeedsEventRow
                key={row.key}
                row={row}
                eventName={eventName}
                busy={busy}
                onAssign={onAssign}
              />
            ))}
          </ul>
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-4 py-3">Metric</th>
              <th className="px-4 py-3 text-right">VendorOrbis</th>
              <th className="px-4 py-3 text-right">Square Direct</th>
              <th className="px-4 py-3 text-right">Difference</th>
            </tr>
          </thead>
          <tbody className="divide-y bg-white">
            {metrics.map((row) => (
              <tr key={row.label}>
                <td className="px-4 py-3 font-semibold">{row.label}</td>
                <td className="px-4 py-3 text-right">
                  {row.count ? row.vendor : money(row.vendor)}
                </td>
                <td className="px-4 py-3 text-right">
                  {row.count ? row.square : money(row.square)}
                </td>
                <td
                  className={`px-4 py-3 text-right font-bold ${
                    row.difference === 0 ? "text-emerald-700" : "text-red-700"
                  }`}
                >
                  {row.count ? row.difference : money(row.difference)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="rounded-xl border border-slate-200 p-4">
        <summary className="cursor-pointer font-bold">
          Transaction matching audit ({reconciliation.audit.length})
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">VendorOrbis ID</th>
                <th className="px-3 py-2">Square payment / refund</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2 text-right">VendorOrbis</th>
                <th className="px-3 py-2 text-right">Square</th>
                <th className="px-3 py-2 text-right">Difference</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {reconciliation.audit.map((row) => (
                <tr key={row.key}>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-1 font-bold ${
                        row.status === "MATCHED"
                          ? "bg-emerald-100 text-emerald-800"
                          : row.status === "MISSING_SQUARE_ID" ||
                              row.status === "MANUAL_SQUARE_ENTRY"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-red-100 text-red-800"
                      }`}
                    >
                      {statusLabel[row.status] || row.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {new Date(row.occurredAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {row.vendorTransactionId || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {row.squareRefundId || row.squarePaymentId || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {row.eventId ? eventName(row.eventId) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(row.vendorAmountCents)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(row.squareAmountCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {money(row.differenceCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
